import { Suspense, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation, useMatch } from "react-router";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useConfigurationLoader } from "../root/useConfigurationLoader";
import Header from "./Header";
import {
  PageActionsProvider,
  PageActionsSlot,
  PageActionsTrailingSlot,
} from "@/components/atomic-crm/layout/PageActions";
import { CrmAssistantButton } from "@/modules/assistant";
import { SpotlightSearchButton } from "@/components/atomic-crm/layout/SpotlightSearchButton";
import { GlobalQuickCreateMenu } from "@/components/atomic-crm/layout/GlobalQuickCreateMenu";
import { GlobalCommsHeaderActions } from "@/components/atomic-crm/layout/GlobalCommsHeaderActions";
import { NotificationCenterButton } from "@/modules/notifications/NotificationCenterButton";
import {
  isProposalFocusModePath,
  isProposalPreviewPath,
} from "@/modules/proposals/proposalFocusMode";
import { isBillingInvoiceWorkspace } from "@/modules/billing/billingWorkspaceMode";
import { DraftsTray } from "@/modules/drafts/DraftsTray";
import { WorkspaceTabBar } from "@/modules/workspace/WorkspaceChrome";
import { useWorkspaceTabBarVisible } from "@/modules/workspace/workspaceTabsContext";

export const Layout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  const location = useLocation();
  const matchDealShow = useMatch("/deals/:id/show");
  const matchMessages = useMatch("/messages");
  const isSettingsShell = Boolean(useMatch("/settings"));
  const isMessagesShell = Boolean(matchMessages);
  const isProposalFocusMode = isProposalFocusModePath(location.pathname);
  const isProposalPreview = isProposalPreviewPath(location.pathname);
  const isBillingInvoiceShell = isBillingInvoiceWorkspace(
    location.pathname,
    location.search,
  );
  const isProjectShowShell = Boolean(matchDealShow);
  const hideGlobalSearch = isProposalFocusMode;
  // Only the client-facing proposal preview hides the top bar.
  const hideGlobalHeader = isProposalPreview;

  const pageHeaderClass =
    "flex shrink-0 flex-col border-b bg-background print:hidden";
  const pageHeaderRowClass = "flex h-11 items-center gap-3 pr-2 pl-4";
  // Wide screens: tabs share the row with the page actions (one slim bar).
  // Narrow screens: tabs get their own row under it.
  const tabsVisible = useWorkspaceTabBarVisible();
  const pageActionsClass = cn(
    "flex min-w-0 flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
    tabsVisible && "min-[1100px]:max-w-[40%] min-[1100px]:flex-none",
  );

  const pageHeader = hideGlobalHeader ? null : !hideGlobalSearch ? (
    <header className={pageHeaderClass}>
      <div className={pageHeaderRowClass}>
        <PageActionsSlot className={pageActionsClass} />
        <WorkspaceTabBar
          placement="inline"
          className="hidden min-[1100px]:flex"
        />
        <div className="flex shrink-0 items-center gap-2">
          <PageActionsTrailingSlot className="flex items-center" />
          <GlobalQuickCreateMenu />
          <GlobalCommsHeaderActions />
          <CrmAssistantButton />
          <SpotlightSearchButton />
          <NotificationCenterButton />
        </div>
      </div>
      <WorkspaceTabBar placement="header" className="min-[1100px]:hidden" />
    </header>
  ) : (
    <header className={pageHeaderClass}>
      <div className={pageHeaderRowClass}>
        <PageActionsSlot className={pageActionsClass} />
        <WorkspaceTabBar
          placement="inline"
          className="hidden min-[1100px]:flex"
        />
        <PageActionsTrailingSlot className="ml-auto flex items-center" />
        <GlobalQuickCreateMenu />
        <GlobalCommsHeaderActions />
        <CrmAssistantButton />
        <SpotlightSearchButton variant="hidden" />
        <NotificationCenterButton />
      </div>
      <WorkspaceTabBar placement="header" className="min-[1100px]:hidden" />
    </header>
  );

  const mainContentPadding = cn(
    "flex min-h-0 flex-1 flex-col print:block print:px-0 print:pt-0 print:pb-0",
    isMessagesShell
      ? "gap-0 p-4"
      : isSettingsShell
        ? "gap-0 p-0 overflow-hidden"
        : isProposalPreview
          ? "gap-0 p-0"
          : isBillingInvoiceShell
            ? "gap-0 p-0"
            : isProjectShowShell
              ? "flex min-h-0 flex-1 flex-col p-4"
              : "gap-0 p-0",
  );

  return (
    <PageActionsProvider>
      <div className="flex h-dvh min-h-0 flex-col overflow-hidden print:h-auto print:overflow-visible">
        <Header />
        {hideGlobalHeader ? <SpotlightSearchButton variant="hidden" /> : null}
        {hideGlobalHeader ? <CrmAssistantButton variant="fab" /> : null}
        {pageHeader}
        <main
          className={cn(
            "flex min-h-0 w-full flex-1 flex-col overflow-hidden",
            mainContentPadding,
          )}
          id="main-content"
        >
          {hideGlobalHeader ? <WorkspaceTabBar placement="strip" /> : null}
          <div
            className={cn(
              "min-h-0 min-w-0 flex-1",
              isMessagesShell ||
                isProposalPreview ||
                isBillingInvoiceShell ||
                isProjectShowShell
                ? "overflow-hidden"
                : isSettingsShell
                  ? "overflow-y-auto overscroll-contain"
                  : // Same 16px inset on all four sides, inside the scroll area so
                    // the bottom keeps its gap when the page scrolls.
                    "overflow-y-auto overscroll-contain px-4 pt-3 pb-4",
            )}
          >
            <ErrorBoundary FallbackComponent={Error}>
              <Suspense
                fallback={<Skeleton className="h-12 w-12 rounded-full" />}
              >
                {children}
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
        <Notification />
        <DraftsTray />
      </div>
    </PageActionsProvider>
  );
};
